import { useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Run ONCE on module load — beats the browser's auto scroll restoration
// which runs immediately on navigation.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const hardScrollTop = () => {
  const html = document.documentElement;
  const previous = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  document.body.scrollTop = 0;
  html.scrollTop = 0;
  requestAnimationFrame(() => { html.style.scrollBehavior = previous; });
};

/**
 * Try to scroll a hash target into view. Returns true if found+scrolled.
 * The element may not exist yet right after a route change, so callers
 * retry on subsequent animation frames.
 */
const scrollToHashTarget = (hash) => {
  if (!hash) return false;
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
};

/**
 * On every route change:
 *  - If there's no hash, snap scroll to top before paint.
 *  - If there IS a hash, retry scrollIntoView until the target node exists
 *    (the destination page may take a tick or two to render).
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (!hash) hardScrollTop();
  }, [pathname, hash]);

  useEffect(() => {
    if (!hash) {
      // Second pass for late-loading content
      const id1 = requestAnimationFrame(() => {
        hardScrollTop();
        const id2 = requestAnimationFrame(hardScrollTop);
        return () => cancelAnimationFrame(id2);
      });
      return () => cancelAnimationFrame(id1);
    }

    // Two-phase scroll: smooth-scroll once when the target first exists,
    // then quietly snap-adjust for the next ~2s as async content (widget
    // gallery fetch, images, etc.) shifts the layout.
    let didInitial = false;
    let elapsed = 0;
    const step = 100;
    const total = 2500;
    const tick = () => {
      const id = hash.replace(/^#/, '');
      const el = document.getElementById(id);
      if (!el) {
        elapsed += step;
        if (elapsed < total) setTimeout(tick, step);
        return;
      }
      if (!didInitial) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        didInitial = true;
      } else {
        const rect = el.getBoundingClientRect();
        if (Math.abs(rect.top) > 4) {
          // Snap-correct without smooth so it's invisible to the user.
          const html = document.documentElement;
          const prev = html.style.scrollBehavior;
          html.style.scrollBehavior = 'auto';
          window.scrollTo({ top: window.scrollY + rect.top, left: 0 });
          requestAnimationFrame(() => { html.style.scrollBehavior = prev; });
        }
      }
      elapsed += step;
      if (elapsed < total) setTimeout(tick, step);
    };
    tick();
    return undefined;
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
